import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
} from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import { TaskAttachmentsPanel } from '@/components/tasks/TaskAttachmentsPanel';
import { ApiError, api } from '@/lib/api';
import { ATTACHMENT_ACCEPT, formatAttachmentSize } from '@/lib/company-attachments';
import { uploadTaskAttachmentFile, validateTaskAttachmentFile } from '@/lib/task-attachments';
import {
  dueLandsAt,
  fromDueLocal,
  recordedZoneMisreadsThisMachine,
  taskKindLabel,
  toDueAtString,
} from '@/lib/task-time';
import { useAuth } from '@/lib/use-auth';
import { useUserOptions } from '@/lib/use-user-options';
import { Button, Combobox, Field, Input, Modal, Select, Textarea } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import type {
  Task,
  TaskCreateInput,
  TaskKind,
  TaskState,
  TaskTargetType,
  TaskUpdateInput,
} from '@/lib/types';

export interface TaskFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create. Provided = edit. */
  task?: Task;
  /** The record this task hangs off, when opened from a record panel. Omit for a personal to-do. */
  target?: { type: TaskTargetType; id: number };
  onSaved: (saved: Task) => void;
}

/** Presentational only — no kind moves a stage or triggers anything. */
const KINDS: readonly TaskKind[] = [
  'follow_up',
  'call',
  'email',
  'meeting',
  'document',
  'review',
] as const;

/**
 * ⚠️ *SUPERSEDED 2026-10-12, the first clause only:* "`cancelled` is a real,
 * accepted state, but **nothing produces it automatically** — it exists as a
 * seam for automation that does not exist yet."
 *
 * 🔴 THE AUTOMATION EXISTS. The ITSM task sync maps an ITSM *rejection* onto
 * `cancelled` (owner's decision: done ↔ resolved, cancelled ↔ rejected), so it
 * now arrives unattended as well as being chosen here. ⛔ Nothing about this
 * control changes — it is one of TWO producers rather than the only one.
 *
 * ✅ EVERYTHING ELSE IN THE OLD NOTE IS STILL TRUE AND IS WHY THE OPTION IS
 * EDIT-ONLY: a task always starts `open`, and `state` is not accepted on create
 * at all. Measured — `createTaskSchema` declares no `state` key, so a create
 * carrying one is a 422 naming an UNRECOGNISED KEY and would refuse `done`
 * identically. ⚠️ That is not a refusal of the value, and reading it as one
 * would make somebody conclude a cancel is impossible over HTTP when PATCH
 * accepts it.
 */
const STATES: readonly { value: TaskState; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

/** The one payload key each target type writes to. At most ONE of these travels. */
const TARGET_KEYS: Record<
  TaskTargetType,
  'dealId' | 'quoteId' | 'invoiceId' | 'deliverableId' | 'companyId'
> = {
  deal: 'dealId',
  quote: 'quoteId',
  invoice: 'invoiceId',
  deliverable: 'deliverableId',
  company: 'companyId',
};

const TARGET_LABELS: Record<TaskTargetType, string> = {
  deal: 'Deal',
  quote: 'Quote',
  invoice: 'Invoice',
  deliverable: 'Deliverable',
  company: 'Company',
};

/**
 * Request keys this form has a control for, so a 422 can be routed to one.
 *
 * `ApiError.fieldErrors` keys match the request key exactly, so `dueAt`,
 * `assignedToId` and the rest land on the box that produced them. Anything
 * outside this set — a target id, say, which this form shows but never lets
 * anyone retype — has no box to land in and is raised at form level rather
 * than being swallowed.
 */
const FIELD_KEYS = new Set(['title', 'details', 'kind', 'dueAt', 'assignedToId', 'state']);

/** Seeded when a date is picked and no time has been chosen yet. */
const DEFAULT_DUE_TIME = '09:00';

/** A file chosen on a NEW task, held until the task exists and can own it. */
interface PendingFile {
  key: number;
  file: File;
}

/** A file this form refused before it was ever sent, with the reason why. */
interface RefusedFile {
  key: number;
  name: string;
  reason: string;
}

/**
 * What is left after a create whose task SAVED and some of whose files did not.
 * The task exists — this is never worded, or handled, as a failed create.
 */
interface CreatedWithGaps {
  task: Task;
  failed: readonly { name: string; reason: string }[];
}

/** Every value is a string, so the edit baseline can be diffed key by key. */
interface FormValues {
  title: string;
  details: string;
  kind: string;
  /** The two halves of the wall clock. Never combined until submit. */
  dueDate: string;
  dueTime: string;
  assignedToId: string;
  state: string;
}

function toFormValues(task: Task | undefined, defaultAssigneeId: string): FormValues {
  const due = fromDueLocal(task?.due ?? null);
  return {
    title: task?.title ?? '',
    details: task?.details ?? '',
    kind: task?.kind ?? 'follow_up',
    dueDate: due.date,
    dueTime: due.time,
    // On create the signed-in user is also the server's default. Showing it is
    // clearer than a blank box that silently resolves to the same person.
    assignedToId: task ? (task.assignedTo ? String(task.assignedTo.id) : '') : defaultAssigneeId,
    state: task?.state ?? 'open',
  };
}

export function TaskFormModal({
  open,
  onClose,
  task,
  target,
  onSaved,
}: TaskFormModalProps): ReactElement | null {
  if (!open) return null;
  return (
    <TaskForm
      key={task?.id ?? 'new'}
      onClose={onClose}
      task={task}
      target={target}
      onSaved={onSaved}
    />
  );
}

function TaskForm({
  onClose,
  task,
  target,
  onSaved,
}: {
  onClose: () => void;
  task?: Task;
  target?: { type: TaskTargetType; id: number };
  onSaved: (saved: Task) => void;
}) {
  const formId = useId();
  const titleRef = useRef<HTMLElement | null>(null);
  const isEdit = task !== undefined;

  const { user } = useAuth();
  const { users, loading: usersLoading } = useUserOptions(true);

  const baseline = useMemo(() => toFormValues(task, user ? String(user.id) : ''), [task, user]);
  const [values, setValues] = useState<FormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /*
   * A ref as well as state: state is batched, so two clicks in one tick would
   * both read `submitting === false` and POST two tasks.
   */
  const submittingRef = useRef(false);

  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [refusedFiles, setRefusedFiles] = useState<RefusedFile[]>([]);
  const nextFileKey = useRef(0);
  /*
   * Set once a create has saved the task and one or more files did not attach.
   * From then on the form is spent: offering Create again would raise a second
   * task, so the modal shows only the outcome and a way to close.
   */
  const [createdWithGaps, setCreatedWithGaps] = useState<CreatedWithGaps | null>(null);
  const uploadAbort = useRef<AbortController | null>(null);

  useEffect(() => () => uploadAbort.current?.abort(), []);

  function chooseFiles(event: ChangeEvent<HTMLInputElement>) {
    const chosen = Array.from(event.target.files ?? []);
    // Cleared so the same file can be picked again after it is removed.
    event.target.value = '';
    if (chosen.length === 0) return;
    const accepted: PendingFile[] = [];
    const refused: RefusedFile[] = [];
    for (const file of chosen) {
      const key = nextFileKey.current++;
      const reason = validateTaskAttachmentFile(file);
      if (reason === null) accepted.push({ key, file });
      else refused.push({ key, name: file.name, reason });
    }
    setPendingFiles((prev) => [...prev, ...accepted]);
    setRefusedFiles(refused);
  }

  function removePendingFile(key: number) {
    setPendingFiles((prev) => prev.filter((entry) => entry.key !== key));
  }

  /**
   * ⚠️ `onSaved` is a CLOSE on `/tasks` (its handler clears the form state), so
   * it is called only once the reader is done here — never as a notification
   * part-way through, which would unmount the report of which files failed.
   */
  function finishCreated(created: Task) {
    onSaved(created);
    onClose();
  }

  const errors =useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => values[key].trim() !== baseline[key].trim());
  }, [values, baseline]);
  const isDirty = changedKeys.length > 0;

  const dueDate = values.dueDate.trim();
  const dueTime = values.dueTime.trim();

  /*
   * ⚠️ **A DUE TIME IS READ ON THE ASSIGNEE'S CLOCK, NOT THE TYPIST'S**, and
   * between the 2026-09-05 sweep and today nothing on this form said so. The
   * three sentences that did — the assignee hint, the reassignment line and the
   * Due hint — were removed along with `zoneById`, `assigneeZone` and
   * `assigneeName`. The BEHAVIOUR never changed; only the explanation went.
   *
   * 🔴 **THAT IS THE "I SET IT FOR FIVE MINUTES AWAY AND IT CAME UP OVERDUE"
   * REPORT** (Daryna, 4 September 2026), reproduced against production on the
   * 11th: every one of the 15 staff accounts carries `Asia/Kolkata`, which is
   * the column default nobody has ever changed. So somebody in London types
   * 02:09 meaning their own clock, the server correctly reads it as 02:09 in
   * India, and the task is born four and a half hours late.
   *
   * ⚠️ **The root cause is DATA — the recorded zones are wrong** — and the fix
   * for that is the owner supplying real zones. What is fixed HERE is the other
   * half: the failure was invisible until after the save. `dueWarning` makes
   * the instant visible BEFORE it, on the reader's own clock.
   */

  /**
   * The timezone recorded against whoever this task is being handed to.
   *
   * ⚠️ **Read off the DIRECTORY page, never off `task.assignedTo`** — that
   * embedded summary is `{id, name, email}` and carries no zone at all, so a
   * reader written against it would answer `undefined` for every existing
   * task and this whole warning would go quiet on exactly the edit path where
   * the assignee is most likely to be changing.
   */
  const assigneeZone = useMemo(() => {
    const id = values.assignedToId.trim();
    if (id === '') return null;
    return users.find((row) => String(row.id) === id)?.timezone ?? null;
  }, [users, values.assignedToId]);

  /**
   * What to say about the due time before it is saved, or `null` when there is
   * nothing worth saying.
   *
   * Two conditions earn a line, and they overlap rather than exclude:
   *
   * - **`alreadyPast`** — the instant this resolves to has gone. Said whatever
   *   the zones are, because a task born overdue is wrong even when the reason
   *   is that somebody typed yesterday's date.
   * - **`zoneIsSuspect`** — the assignee's RECORDED zone is a different clock
   *   from the machine this is being typed on, so the hours the reader is
   *   picturing are not the hours the server will store.
   *
   * ⚠️ **Silence is the answer when the zone cannot be read**, never a warning.
   * An unassigned task has no clock (the form already refuses a due time
   * without an assignee), a directory page that has not loaded is not evidence
   * of a mismatch, and a warning nobody can act on trains people to ignore the
   * one that matters.
   */
  const dueWarning = useMemo(() => {
    if (dueDate === '' || dueTime === '' || assigneeZone === null) return null;
    const landing = dueLandsAt(dueDate, dueTime, assigneeZone);
    if (landing === null) return null;
    const zoneIsSuspect = recordedZoneMisreadsThisMachine(assigneeZone);
    if (!zoneIsSuspect && !landing.alreadyPast) return null;
    /*
     * No zone argument, deliberately: this is the one figure on the form that
     * is deliberately rendered on the READER's clock, because the whole point
     * is to let them compare it against the time they just typed.
     */
    let onYourClock: string;
    try {
      onYourClock = landing.instant.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return null;
    }
    return { zoneIsSuspect, alreadyPast: landing.alreadyPast, onYourClock };
  }, [dueDate, dueTime, assigneeZone]);

  function setField(key: keyof FormValues, next: string) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  /** The due pair reports as one field, `dueAt`, because that is the request key. */
  function setDueField(key: 'dueDate' | 'dueTime', next: string) {
    setValues((prev) => {
      const draft = { ...prev, [key]: next };
      // A date on its own is not a wall clock. Rather than reject the half the
      // user has not thought about yet, seed a working-morning time they can
      // then change.
      if (key === 'dueDate' && next !== '' && prev.dueTime.trim() === '') {
        draft.dueTime = DEFAULT_DUE_TIME;
      }
      return draft;
    });
    setClientErrors((prev) => ('dueAt' in prev ? without(prev, 'dueAt') : prev));
    setServerErrors((prev) => ('dueAt' in prev ? without(prev, 'dueAt') : prev));
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    const title = values.title.trim();
    if (title === '') found.title = 'A title is required.';

    if (dueDate !== '' && dueTime === '') {
      found.dueAt = 'Pick a time as well.';
    } else if (dueDate === '' && dueTime !== '') {
      found.dueAt = 'Pick a date as well.';
    } else if (dueDate !== '' && dueTime !== '') {
      // Returns null for a date the calendar does not have, e.g. 2026-02-30,
      // which is refused here rather than sent and bounced.
      if (toDueAtString(dueDate, dueTime) === null) {
        found.dueAt = 'Not a real date.';
      } else if (values.assignedToId === '') {
        /*
         * The server says this too, at path `assignedToId`, but saying it here
         * explains WHY rather than only refusing: the due time is expressed on
         * the assignee's clock, so with nobody assigned there is no clock.
         */
        found.assignedToId = 'A due time needs an assignee - it reads on their clock.';
      }
    }

    return found;
  }

  /*
   * Both bodies are strict: an unknown key is a 422 naming it, not a silent
   * strip. So every payload is assembled from an explicit key list, never
   * spread in from state or a loaded record.
   *
   * `completedBy` and `completedAt` are never sent — the server stamps them
   * on close and clears them on re-open.
   */
  function buildCreate(): TaskCreateInput {
    const payload: TaskCreateInput = { title: values.title.trim() };

    const details = values.details.trim();
    if (details !== '') payload.details = details;

    payload.kind = values.kind as TaskKind;

    // Blank means the user chose "Unassigned" over the pre-filled default, so
    // it travels as an explicit null rather than being omitted — omitting it
    // would hand the task straight back to the caller.
    payload.assignedToId = values.assignedToId === '' ? null : Number(values.assignedToId);

    if (dueDate !== '' && dueTime !== '') {
      payload.dueAt = toDueAtString(dueDate, dueTime);
    }

    // At most ONE target: sending a second is a 422 naming it. The caller hands
    // over the record its panel was opened on, and no other is offered.
    if (target) payload[TARGET_KEYS[target.type]] = target.id;

    // `state` is deliberately absent — it is not accepted on create, and a task
    // always starts `open`.
    return payload;
  }

  function buildUpdate(): TaskUpdateInput {
    const payload: TaskUpdateInput = {};

    for (const key of changedKeys) {
      if (key === 'title') payload.title = values.title.trim();
      else if (key === 'details') {
        payload.details = values.details.trim() === '' ? null : values.details.trim();
      } else if (key === 'kind') payload.kind = values.kind as TaskKind;
      else if (key === 'state') payload.state = values.state as TaskState;
      else if (key === 'assignedToId') {
        payload.assignedToId = values.assignedToId === '' ? null : Number(values.assignedToId);
      }
      // dueDate / dueTime are two controls over one request key, handled once
      // below so the pair can never be sent as two separate thoughts.
    }

    if (changedKeys.includes('dueDate') || changedKeys.includes('dueTime')) {
      payload.dueAt = dueDate === '' && dueTime === '' ? null : toDueAtString(dueDate, dueTime);
    }

    // The four target ids are accepted here, but this form offers no picker for
    // them: moving a task between records clears the others, which is a
    // different action from editing this one, and not one to be stumbled into
    // from a panel opened on a single record.
    return payload;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    // This modal is opened from record panels that are themselves modals.
    // React portals propagate by the React tree rather than the DOM one, so
    // without this the submit also surfaces on the outer form and saves a
    // half-filled record alongside the task.
    event.stopPropagation();
    if (submittingRef.current || createdWithGaps !== null) return;

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    const payload = isEdit ? buildUpdate() : buildCreate();
    // A PATCH needs at least one key, and Save is already disabled while
    // nothing has changed. This is the belt to that pair of braces.
    if (isEdit && Object.keys(payload).length === 0) {
      setFormError('Nothing has changed yet.');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    let result: { data: Task };
    try {
      result = task
        ? await api.updateTask(task.id, payload as TaskUpdateInput)
        : await api.createTask(payload as TaskCreateInput);
    } catch (caught) {
      if (caught instanceof ApiError) {
        const mapped: Record<string, string> = {};
        // Seeded with the messages that belong to no field. A `.strict()`
        // rejection names the offending key with an EMPTY path, so it can
        // only be shown at form level — keyed by path it would render nowhere.
        const stray: string[] = [...caught.formMessages];
        for (const [key, message] of Object.entries(caught.fieldErrors)) {
          if (FIELD_KEYS.has(key)) mapped[key] = message;
          else stray.push(`${key}: ${message}`);
        }
        if (Object.keys(mapped).length > 0) setServerErrors(mapped);
        if (stray.length > 0) setFormError(stray.join(' '));
        else if (Object.keys(mapped).length === 0) setFormError(caught.message);
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    const saved = result.data;
    if (isEdit || pendingFiles.length === 0) {
      onSaved(saved);
      onClose();
      return;
    }

    /*
     * The task EXISTS from here on. A file needs the task's id, so the uploads
     * can only follow the create, and there is no transaction spanning the two
     * — nothing below may be worded as though the task were undone.
     */
    const controller = new AbortController();
    uploadAbort.current = controller;
    const failed: { name: string; reason: string }[] = [];
    for (const { file } of pendingFiles) {
      try {
        await uploadTaskAttachmentFile(saved.id, file, controller.signal);
      } catch (caught) {
        if (controller.signal.aborted) return;
        failed.push({
          name: file.name,
          reason:
            caught instanceof ApiError
              ? caught.formMessage ?? caught.message
              : 'Could not reach the server.',
        });
      }
    }
    if (controller.signal.aborted) return;

    if (failed.length === 0) {
      onSaved(saved);
      onClose();
      return;
    }
    setPendingFiles([]);
    setRefusedFiles([]);
    setCreatedWithGaps({ task: saved, failed });
    submittingRef.current = false;
    setSubmitting(false);
  }

  const userOptions = useMemo<ComboboxOption[]>(() => {
    const list = users.map((row) => ({
      value: String(row.id),
      label: row.name,
      // The zone is the hint rather than the email: it is what decides when a
      // due time on this task actually falls.
      hint: row.timezone,
    }));
    // The current assignee may sit outside the fetched page. Losing them from
    // the list reads as "unassigned" and saves as a null that both drops the
    // person and orphans the due time.
    if (task?.assignedTo && !list.some((option) => option.value === String(task.assignedTo?.id))) {
      list.unshift({
        value: String(task.assignedTo.id),
        label: task.assignedTo.name,
        hint: task.assignedTo.email,
      });
    }
    return list;
  }, [users, task]);

  const shownTarget = task?.target ?? target ?? null;
  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = isEdit ? !isDirty : values.title.trim() === '';

  if (createdWithGaps !== null) {
    const { task: created, failed } = createdWithGaps;
    const done = () => finishCreated(created);
    return (
      <Modal
        open
        onClose={done}
        size="md"
        dismissOnBackdrop={false}
        title={`Created ${created.title}`}
        footer={
          <Button variant="primary" onClick={done}>
            Done
          </Button>
        }
      >
        <div role="alert" className="space-y-3 text-sm text-fg">
          <p>
            The task was created, but{' '}
            {failed.length === 1 ? 'this file did not attach' : `these ${failed.length} files did not attach`}.
            {' '}They can be added by editing the task.
          </p>
          <ul className="space-y-1.5">
            {failed.map((entry, index) => (
              <li key={`${entry.name}-${index}`}>
                <Notice
                  tone="danger"
                  title={
                    <span className="block truncate" title={entry.name}>
                      {entry.name}
                    </span>
                  }
                >
                  <span className="block text-xs">{entry.reason}</span>
                </Notice>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      // Closing mid-upload would abort the remaining files and leave a created
      // task with no report of what did not attach, so it waits for the run.
      onClose={submitting && !isEdit && pendingFiles.length > 0 ? () => undefined : onClose}
      size="md"
      dismissOnBackdrop={false}
      initialFocusRef={titleRef}
      title={task ? `Edit ${task.title}` : 'New task'}
      footer={
        <>
          {isEdit && !isDirty && (
            <p className="self-center text-xs text-fg-muted sm:mr-auto">No changes yet.</p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create task'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <div
          ref={(node) => {
            titleRef.current = node?.querySelector('input') ?? null;
          }}
        >
          <Field label="Title" required error={errors.title}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                value={values.title}
                invalid={Boolean(errors.title)}
                aria-describedby={describedBy}
                onChange={(event) => setField('title', event.target.value)}
              />
            )}
          </Field>
        </div>

        <Field label="Details" error={errors.details}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Textarea
              id={id}
              value={values.details}
              rows={3}
              invalid={Boolean(errors.details)}
              aria-describedby={describedBy}
              onChange={(event) => setField('details', event.target.value)}
            />
          )}
        </Field>

        {!isEdit && (
          <div className="space-y-2">
            <Field label="Attach files">
              {({ id, 'aria-describedby': describedBy }) => (
                /*
                 * A native file input, as on `CompanyAttachmentsPanel`: `Input`
                 * is a text control and has no file form. Styled from tokens
                 * only, copied from there so the two pickers match.
                 */
                <input
                  id={id}
                  aria-describedby={describedBy}
                  type="file"
                  multiple
                  accept={ATTACHMENT_ACCEPT}
                  disabled={submitting}
                  onChange={chooseFiles}
                  className="w-full cursor-pointer rounded-md border border-border bg-surface px-2.5 py-2 text-sm text-fg file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-surface-sunken file:px-2 file:py-1 file:text-xs file:text-fg-secondary disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-subtle"
                />
              )}
            </Field>

            {refusedFiles.length > 0 && (
              <ul role="alert" className="space-y-1">
                {refusedFiles.map((entry) => (
                  <li key={entry.key} className="text-xs text-danger">
                    <span className="font-medium">{entry.name}</span>: {entry.reason}
                  </li>
                ))}
              </ul>
            )}

            {pendingFiles.length > 0 && (
              <ul className="space-y-1">
                {pendingFiles.map((entry) => (
                  <li
                    key={entry.key}
                    className="flex items-center gap-2 rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate text-fg" title={entry.file.name}>
                      {entry.file.name}
                    </span>
                    <span className="shrink-0 text-xs text-fg-muted">
                      {formatAttachmentSize(entry.file.size)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={submitting}
                      aria-label={`Remove ${entry.file.name}`}
                      onClick={() => removePendingFile(entry.key)}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Kind" error={errors.kind}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                value={values.kind}
                invalid={Boolean(errors.kind)}
                aria-describedby={describedBy}
                onChange={(event) => setField('kind', event.target.value)}
              >
                {KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {taskKindLabel(kind)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {isEdit && (
            <Field label="State" error={errors.state}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Select
                  id={id}
                  value={values.state}
                  invalid={Boolean(errors.state)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('state', event.target.value)}
                >
                  {STATES.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
        </div>

        <Field label="Assigned to" error={errors.assignedToId}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              value={values.assignedToId}
              onChange={(next) => setField('assignedToId', next)}
              options={userOptions}
              loading={usersLoading}
              emptyOptionLabel="Unassigned"
              searchPlaceholder="Search staff…"
              invalid={Boolean(errors.assignedToId)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field label="Due" error={errors.dueAt}>
          {({ id, 'aria-describedby': describedBy }) => (
            <div className="grid grid-cols-2 gap-2">
              <Input
                id={id}
                // Native date and time inputs emit YYYY-MM-DD and HH:MM from
                // local parts. No Date is constructed anywhere on this path, so
                // no zone is ever attached to what gets sent.
                type="date"
                value={values.dueDate}
                invalid={Boolean(errors.dueAt)}
                aria-describedby={describedBy}
                aria-label="Due date"
                onChange={(event) => setDueField('dueDate', event.target.value)}
              />
              <Input
                type="time"
                value={values.dueTime}
                invalid={Boolean(errors.dueAt)}
                aria-describedby={describedBy}
                aria-label="Due time"
                onChange={(event) => setDueField('dueTime', event.target.value)}
              />
            </div>
          )}
        </Field>

        {/*
          The clock a due time is read on, said before the save rather than
          discovered as a task that arrived late. See `dueWarning` above for
          why each half fires and why silence is the default.
        */}
        {dueWarning !== null && (
          <p
            className={`text-xs ${dueWarning.alreadyPast ? 'text-danger' : 'text-fg-muted'}`}
          >
            {dueWarning.alreadyPast
              ? `This falls due in the past — ${dueWarning.onYourClock} on your own clock, so it will show as overdue straight away.`
              : `Read on ${assigneeZone}, the timezone recorded for this assignee — ${dueWarning.onYourClock} on your own clock.`}
            {dueWarning.zoneIsSuspect && (
              <>
                {' '}
                The time you type is read on <strong>{assigneeZone}</strong>,
                which is not the timezone this browser is on. If that is not
                where they work, an administrator can correct it on their user
                record.
              </>
            )}
          </p>
        )}

        {/*
         * The attached record, shown and not editable. Nothing here contributes
         * a key to the payload — see the note at the end of `buildUpdate`.
         */}
        {shownTarget && (
          <p className="text-xs text-fg-muted">
            Attached to {TARGET_LABELS[shownTarget.type].toLowerCase()} #{shownTarget.id}.
          </p>
        )}
      </form>

      {/*
        Outside the <form>, deliberately: the panel carries controls of its own,
        and React propagates submit by the React tree, so inside it a panel
        action could surface as this form's submit. Each attachment is written
        by the panel on its own request — the Save button above does not touch
        them.
      */}
      {task && (
        <div className="mt-5">
          <TaskAttachmentsPanel taskId={task.id} canEdit />
        </div>
      )}
    </Modal>
  );
}
